import { RefreshCw, Eye, MessageSquare, Image, Phone, ClipboardList, Smartphone, CreditCard, ArrowUpRight, FileText } from 'lucide-react';

interface UsageCategory {
  category: string;
  count: number;
  unit: string;
  total_cost: number;
}

interface UsageSummaryProps {
  categories: UsageCategory[];
  totalCost: number;
  loading?: boolean;
  error?: string | null;
  onViewDetails?: (category: string) => void;
}

// Category display configuration
const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  sms_outbound: { label: 'SMS Outbound', icon: <MessageSquare className="w-5 h-5" /> },
  sms_inbound: { label: 'SMS Inbound', icon: <MessageSquare className="w-5 h-5" /> },
  mms_outbound: { label: 'MMS Outbound', icon: <Image className="w-5 h-5" /> },
  mms_inbound: { label: 'MMS Inbound', icon: <Image className="w-5 h-5" /> },
  voice_outbound: { label: 'Voice Outbound', icon: <Phone className="w-5 h-5" /> },
  voice_inbound: { label: 'Voice Inbound', icon: <Phone className="w-5 h-5" /> },
  list_scrubber: { label: 'List Scrubber Processing', icon: <ClipboardList className="w-5 h-5" /> },
  phone_lines: { label: 'Phone Lines', icon: <Smartphone className="w-5 h-5" /> },
  plan_charges: { label: 'Plan Charges', icon: <CreditCard className="w-5 h-5" /> },
  transfer_out: { label: 'Transfer Sent', icon: <ArrowUpRight className="w-5 h-5" /> },
  a2p_registration_fee: { label: 'A2P Registration', icon: <FileText className="w-5 h-5" /> },
};

/**
 * Usage Summary table by category
 * Shows all 9 usage categories with count, cost, and details button
 */
export function UsageSummary({ categories, totalCost, loading, error, onViewDetails }: UsageSummaryProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatCount = (count: number, unit: string) => {
    return `${count.toLocaleString()} ${unit}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Usage Summary</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Usage Summary</h2>
        </div>
        <div className="p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Usage Summary</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Category</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Count</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total Cost</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map((cat) => {
              const config = CATEGORY_CONFIG[cat.category] || { label: cat.category, icon: null };
              return (
                <tr key={cat.category} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{config.icon}</span>
                      <span className="text-sm text-gray-900">{config.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCount(cat.count, cat.unit)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className="text-orange-600 font-medium">{formatCurrency(cat.total_cost)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onViewDetails?.(cat.category)}
                      className="inline-flex items-center justify-center w-9 h-9 border-2 border-blue-500 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors"
                      title="View details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Totals Row */}
            <tr className="bg-gray-50 border-t-2 border-gray-200">
              <td className="px-4 py-3 text-sm font-bold text-gray-900">Total Usage</td>
              <td className="px-4 py-3 text-sm text-right text-gray-400">-</td>
              <td className="px-4 py-3 text-sm text-right font-bold text-orange-600">
                {formatCurrency(totalCost)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
